import React, { useState, useEffect, useMemo } from 'react';
import { fetchUserInfo, formatDate, apiRequest, groupSponsorsByType, teamPhoto } from '../../utils';
import InstallApp from '../../components/common/InstallApp';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../components/common/Icon';
import CheckInCard from '../../components/common/CheckInCard';
import MeetQrSheet from '../../components/common/MeetQrSheet';
import MyBreakout from '../../components/common/MyBreakout';
import Title from '../../components/common/Title';
import Countdown from '../../components/elements/Countdown';
import ContactPersonNotice from '../../components/common/ContactPersonNotice';
import ContactPersonChoiceModal from '../../components/common/ContactPersonChoiceModal';
import AnnouncementModal from '../../components/common/AnnouncementModal';
import StatTile from '../../components/common/StatTile';
import MyReferralCode from '../../components/common/MyReferralCode';
import Base from '../Base';
import RecapImagesButton from '../team/RecapImagesButton';
import './Home.css';

const fallback = {
  company: require('./../../assets/images/fallback/company.png'),
  user: require('./../../assets/images/fallback/user.png'),
  activity: require('./../../assets/images/fallback/activity.png'),
  mission: require('./../../assets/images/fallback/mission.png'),
  event: require('./../../assets/images/fallback/event.png'),
};

// Articles shown on the home itself; the rest sit behind "Show more" on /blogs.
const HOME_BLOG_COUNT = 3;

/* ----------------------- small helpers ----------------------- */

const Section = ({ icon, title, to, seeAllKey = 'seeAll', children }) => {
  const { t } = useTranslation();
  return (
    <div className="home-section">
      <div className="home-section__head">
        <h3 className="home-section__title">
          {icon && <Icon name={icon} theme="primary" />} {title}
        </h3>
        {to && (
          <Link to={to} className="home-section__see-all">
            {t(seeAllKey)} <Icon name="angle-right" />
          </Link>
        )}
      </div>
      <div className="home-scroll">{children}</div>
    </div>
  );
};

const Tile = ({ to, onClick, image, contain = false, title, sub, badge }) => {
  // URLs from blob storage can contain spaces; an unquoted CSS url() with a
  // space silently fails to load and leaves the tile blank. Encode + quote.
  const safeImage =
    typeof image === 'string' && image.includes(' ') ? encodeURI(image) : image;
  return (
    <Link to={to || '#'} onClick={onClick} className="home-tile">
      <div
        className={`home-tile__media ${contain ? 'home-tile__media--contain' : ''}`}
        style={{ backgroundImage: `url("${safeImage}")` }}
      >
        {badge && (
          <span className="home-tile__badge">
            {badge.icon && <Icon name={badge.icon} pack={badge.pack || 'br'} />}
            {badge.text}
          </span>
        )}
      </div>
      <div className="home-tile__body">
        <p className="home-tile__title">{title}</p>
        {sub && <p className="home-tile__sub">{sub}</p>}
      </div>
    </Link>
  );
};

/* ----------------------- main page ----------------------- */

const Home = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(null);
  // Networking QR: shown in a sheet rather than on a page, because it is held up for three
  // seconds and dismissed — a route change would make going back a decision.
  const [showMeetQr, setShowMeetQr] = useState(false);
  const [, setLoading] = useState(true);
  const [, setError] = useState(null);

  const [homeTeams, setHomeTeams] = useState([]);
  const [homeBattles, setHomeBattles] = useState([]);
  const [homeCompanies, setHomeCompanies] = useState([]);
  const [homeCommunities, setHomeCommunities] = useState([]);
  const [homeParticipants, setHomeParticipants] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [isCheers, setCheers] = useState(false);
  const [ownCheerCount, setOwnCheerCount] = useState(0);
  const [ownCheerRank, setOwnCheerRank] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [missions, setMissions] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [socialMissions, setSocialMissions] = useState([]);
  const [albumItems, setAlbumItems] = useState([]);
  const [awards, setAwards] = useState([]);
  const [eventSurveys, setEventSurveys] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [leaderboard, setLeaderboard] = useState(null);
  const [showCpChoice, setShowCpChoice] = useState(false);
  const [captainStatus, setCaptainStatus] = useState(null);
  // The teams this player's OWN order paid for. One team is just "their" team and
  // shows nothing; two or more is a buyer who has a second team to staff.
  const [orderTeams, setOrderTeams] = useState([]);
  const [ticketSummary, setTicketSummary] = useState(null);
  const [pendingJoin, setPendingJoin] = useState([]);
  const [switching, setSwitching] = useState(false);
  // Community leader: her code, her booking link and the teams it brought in.
  const [grow, setGrow] = useState(null);
  const [growCopied, setGrowCopied] = useState(null);   // 'link' | 'code'
  const [showCode, setShowCode] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ first_name: '', last_name: '', email: '' });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState(null);     // {ok: bool, text}

  // Switch the session between the contexts a person holds: a company/community they
  // lead (kind 'contact') or their Bravos membership (kind 'bravos'). The server
  // re-issues the JWT and returns where to go next — /event-select when the chosen
  // company has several events ("welk event"), /home for one, /member-home for Bravos.
  const doSwitchContext = async (item) => {
    if (!item || switching) return;
    if (item.kind !== 'bravos' && item.id === userInfo?.company_id) return;
    setSwitching(true);
    try {
      const res = item.kind === 'bravos'
        ? await apiRequest('POST', '/role', { role: 'bravosMember' })
        : await apiRequest('POST', '/switch-company', { company_id: item.id });
      window.location.assign(res?.redirect || '/home');
    } catch (err) {
      setSwitching(false);
    }
  };

  // "Check out the other event": another event on the same day at the same venue (HT and PLG
  // Dubai share The Sevens Stadium). The server only re-scopes the session — a player there is a
  // player, anyone else a supporter — and a full load picks up the other event's branding.
  const [siblingSwitching, setSiblingSwitching] = useState(false);
  const doSwitchSibling = async (eventId) => {
    if (siblingSwitching) return;
    setSiblingSwitching(true);
    try {
      const res = await apiRequest('POST', '/sibling-events/switch', { event_id: eventId });
      window.location.assign(res?.redirect || '/home');
    } catch (err) {
      setSiblingSwitching(false);
    }
  };

  /* /event-home is this same screen reached from the company admin home ("Manage current event").
     It asks with ?manage=1, so the server does not send a company admin back to that home. */
  const manageEvent = useLocation().pathname === '/event-home';
  useEffect(() => {
    fetchUserInfo(manageEvent ? '/home?manage=1' : '/home', (data) => {
      if (data?.redirect) {
        navigate(data.redirect);
        return;
      }
      // A payload whose role maps to no home hero (or an empty payload) would otherwise
      // leave Home stuck on a permanent loading skeleton — the "blank screen" a returning
      // multi-role user hits when their session context is stale. When we can't determine
      // the screen, fall back to the role picker (it rebuilds context, e.g. → Bravos member).
      if (!data || !['player', 'crew', 'supporter', 'contactPerson'].includes(data.role)) {
        navigate('/role');
        return;
      }
      setUserInfo(data);
    }, setLoading, (err) => { setError(err); navigate('/role'); }, []);
  }, [navigate, manageEvent]);

  const isPlayer = userInfo?.role === 'player';
  const isCrew = userInfo?.role === 'crew';
  const isContact = userInfo?.role === 'contactPerson';
  const isSupporter = userInfo?.role === 'supporter';
  // A crew member whose details the venue still needs (operations request) gets the
  // "My details" pill marked; the details page itself is always one tap away.
  const [crewOpen, setCrewOpen] = useState(null);
  useEffect(() => {
    if (!isCrew) return;
    apiRequest('GET', '/crew-details/open').then((r) => setCrewOpen(r.open)).catch(() => {});
  }, [isCrew]);
  const isPreEvent = !!userInfo?.date && new Date(userInfo.date) >= new Date();
  const isPostEvent = !!userInfo?.date && new Date(userInfo.date) <= new Date();
  const showReadiness = isPlayer && isPreEvent;

  // Make it unmistakable that team features live in the player view: pop the
  // choice modal once per session — but ONLY for a contact person who is ALSO a
  // player (has a team). A pure / first-time contact person (e.g. arrived via the
  // order magic-login) must land straight in the contact view; never risk them
  // tapping "player" and losing the flow. '/role' returns 'player' only if they
  // actually have a team.
  useEffect(() => {
    if (!isContact || sessionStorage.getItem('cpChoiceSeen')) return;
    apiRequest('GET', '/role')
      .then((d) => { if ((d?.roles || []).includes('player')) setShowCpChoice(true); })
      .catch(() => {});
  }, [isContact]);

  const dismissCpChoice = () => {
    sessionStorage.setItem('cpChoiceSeen', '1');
    setShowCpChoice(false);
  };

  // Community leader: copy her booking link or her bare code. Both are the SAME
  // credential — the link just has the code baked in — so whichever she reaches for,
  // the women who use it land in a team attributed to her community.
  const copyGrow = async (what) => {
    const text = what === 'code' ? grow?.code : grow?.booking_link;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setGrowCopied(what);
      setTimeout(() => setGrowCopied(null), 2000);
    } catch {
      // Clipboard is blocked (insecure context / permission) — reveal the code so she
      // can select it by hand rather than leaving her with a button that does nothing.
      setShowCode(true);
    }
  };

  const submitCommunityInvite = async (e) => {
    e.preventDefault();
    if (inviteBusy) return;
    setInviteBusy(true);
    setInviteMsg(null);
    try {
      const res = await apiRequest('POST', '/community/invite', inviteForm);
      setInviteMsg({ ok: true, text: t('communityInviteSent', { name: res?.name || '' }) });
      setInviteForm({ first_name: '', last_name: '', email: '' });
    } catch (err) {
      const map = {
        invalidEmail: t('invalidEmail'),
        noCommunityCode: t('noCommunityCode'),
        noStorefront: t('noStorefront'),
      };
      setInviteMsg({ ok: false, text: map[err.message] || t('somethingWentWrong') });
    } finally {
      setInviteBusy(false);
    }
  };

  useEffect(() => {
    if (!userInfo) return;

    fetchUserInfo('/home_teams', (data) => setHomeTeams(data.teams), setLoading, setError);

    if (isPlayer) {
      if (showReadiness) {
        fetchUserInfo('/team_readiness?own_team=1', (data) => setReadiness(data.readiness), setLoading, setError);
      } else {
        setReadiness(null);
      }

      fetchUserInfo('/missions?own_team=1', (data) => {
        setSubmissions(data.submissions);
        setMissions(data.missions);
      }, setLoading, setError);
      fetchUserInfo('/social_missions?own_team=1', (data) => {
        setSocialMissions(data.missions || []);
      }, setLoading, setError);
      fetchUserInfo('/upcoming_challenges?limit=5', setUpcoming, setLoading, setError);
    }

    // Contact person's FIRST job: a captain on every team. Load which teams
    // still miss one so the "add captains" prompt can push them.
    // A player who BOUGHT more than one team: checkout seats them on the first
    // one, so without this list the others are invisible to them. Silent on
    // failure — a one-team buyer simply gets no card.
    if (isPlayer) {
      apiRequest('GET', '/my-order-teams')
        .then((d) => setOrderTeams(d?.teams || [])).catch(() => {});
    }

    if (isContact) {
      apiRequest('GET', '/company-captains').then(setCaptainStatus).catch(() => {});
      // People waiting for the contact person to accept them into the
      // company/community — surfaced as a prominent banner below.
      apiRequest('GET', '/join-requests').then((d) => setPendingJoin(d?.requests || [])).catch(() => {});
      // A community leader's own screen. 403/409 for a plain company contact person,
      // which the catch turns into "no card" — the endpoint is the gate.
      apiRequest('GET', '/community/grow').then(setGrow).catch(() => {});
      // What the company has actually bought, per edition — the seats they hold and
      // how many still have nobody in them. Silent on failure: a company with no
      // tickets simply shows no card.
      apiRequest('GET', '/shop/tickets').then(setTicketSummary).catch(() => {});
    }

    if (userInfo.event_type === 'Event') {
      fetchUserInfo('/home_battles', (data) => setHomeBattles(data.battles), setLoading, setError);
      fetchUserInfo('/home_companies', (data) => setHomeCompanies(data.companies), setLoading, setError);
      fetchUserInfo('/home_communities', (data) => setHomeCommunities(data.communities), setLoading, setError);
    }
    // People are useful on every home (PLG-style events included), so load
    // participants regardless of event_type — the section only renders when
    // there are any.
    fetchUserInfo('/home_participants', (data) => setHomeParticipants(data.participants), setLoading, setError);

    fetchUserInfo('/awards', (data) => setAwards(data.awards || []), setLoading, setError);
    fetchUserInfo('/event-surveys', (data) => setEventSurveys(data || []), setLoading, setError);

    fetchUserInfo('/home_cheers_lb', (data) => {
      setCheers(data.active);
      setOwnCheerCount(data.own_count || 0);
      setOwnCheerRank(data.own_rank ? { pos: data.own_rank, total: data.total_teams } : null);
    }, setLoading, setError);

    fetchUserInfo('/sponsors', (data) => setSponsors(data.sponsors), setLoading, setError);
    fetchUserInfo('/album', (data) => setAlbumItems(data.photos), setLoading, setError);
    // Blog articles for this business unit. Its own fetch (not part of /home) so a slow or
    // empty feed never holds up the rest of the page — the card simply doesn't render.
    fetchUserInfo('/content-feed', (data) => setBlogs(data.items || []), setLoading, setError);

    if (isPlayer) {
      fetchUserInfo('/leaderboard', (data) => setLeaderboard(data), setLoading, setError);
    }
  }, [userInfo, isPlayer, showReadiness]);

  const teamRank = useMemo(() => {
    if (!leaderboard?.teams || !leaderboard?.user_team_id) return null;
    const idx = leaderboard.teams.findIndex((tm) => tm.id === leaderboard.user_team_id);
    if (idx < 0) return null;
    const team = leaderboard.teams[idx];
    // total_teams is the organiser's count (PLG: ladies still forming a team count as the
    // teams they will make), so it can sit below the rows listed — never below your own place.
    return {
      pos: idx + 1,
      total: Math.max(idx + 1, leaderboard.total_teams ?? leaderboard.teams.length),
      name: team.name,
      points: Math.round(team.points || 0),
    };
  }, [leaderboard]);

  /* derived flags */
  // Home surfaces every mission inside its start/end window, plus the ones the
  // team already handed a photo in for (that photo becomes the tile); the
  // missions page still lists the other out-of-window ones (dimmed).
  const activeMissions = useMemo(() => {
    const subs = Array.isArray(submissions) ? submissions : [];
    return (Array.isArray(missions) ? missions : [])
      .map((m) => ({ ...m, submission: subs.find((s) => s.picture_mission_in_event_id === m.id) }))
      .filter((m) => m.is_active !== false || m.submission);
  }, [missions, submissions]);
  const activeVideoMissions = useMemo(
    () => (Array.isArray(socialMissions) ? socialMissions.filter((m) => m.is_active !== false) : []),
    [socialMissions]
  );
  const hasMissions = isPlayer && activeMissions.length + activeVideoMissions.length > 0;
  const hasAlbum = Array.isArray(albumItems) && albumItems.length > 0;
  const hasAwards = Array.isArray(awards) && awards.length > 0;
  const hasSurveys = Array.isArray(eventSurveys) && eventSurveys.length > 0;
  // Surveys to remind the player about, shown as a section above Teams:
  //   1) user is a player,
  //   2) the team hasn't completed it yet,
  //   3) today falls inside the survey's start/end window.
  const incompleteSurveys = useMemo(() => {
    if (!isPlayer || !Array.isArray(eventSurveys)) return [];
    const now = new Date();
    return eventSurveys.filter((s) => {
      if (s.team_submitted) return false;
      const start = s.start_date ? new Date(s.start_date) : null;
      const end   = s.end_date   ? new Date(s.end_date)   : null;
      if (start && now < start) return false;
      if (end   && now > end)   return false;
      return true;
    });
  }, [isPlayer, eventSurveys]);
  const upcomingList = Array.isArray(upcoming?.upcoming_challenges) ? upcoming.upcoming_challenges : [];
  const featuredNext = upcomingList[0];
  const restUpcoming = upcomingList.slice(1);

  /* hero choice */
  const hero = useMemo(() => {
    if (!userInfo) return { kind: 'loading' };

    if (isPlayer) {
      const img = teamPhoto(userInfo.team_photo_url, userInfo.company_picture);
      const contain = !userInfo.team_photo_url && !!userInfo.company_picture;
      return {
        kind: 'team',
        to: `/public-team/${userInfo.team_id}`,
        image: img,
        contain,
        kicker: t('team'),
        title: userInfo.team_name,
        number: userInfo.team_number,
        subtitle: userInfo.company_name,
        action: 'choose',
      };
    }
    if (isContact) {
      return {
        kind: 'company',
        to: `/public-company/${userInfo.company_id}`,
        image: userInfo.company_picture || fallback.company,
        contain: true,
        kicker: userInfo.is_community ? t('yourCommunity') : t('yourCompany'),
        title: userInfo.company_name,
        subtitle: userInfo.name,
        action: 'angle-right',
      };
    }
    if (isCrew) {
      return {
        kind: 'crew',
        to: '/crew-battles',
        image: userInfo.activity_photo_url || fallback.activity,
        contain: false,
        kicker: t('scoreEntry'),
        title: userInfo.activity_name || t('noBattleYet'),
        subtitle: userInfo.name,
        action: 'angle-right',
      };
    }
    if (isSupporter) {
      return {
        kind: 'supporter',
        to: `/public-event/${userInfo.event_id}`,
        image: userInfo.cover_image || userInfo.photo_url || fallback.event,
        contain: false,
        kicker: t('supporter'),
        title: userInfo.name,
        subtitle: [formatDate(userInfo.date), userInfo.location_name]
          .filter(Boolean)
          .join(' • '),
        action: 'angle-right',
      };
    }
    return { kind: 'loading' };
  }, [userInfo, isPlayer, isContact, isCrew, isSupporter, t]);

  /* quick action pills */
  const pills = useMemo(() => {
    if (!userInfo) return [];
    const list = [];

    // Contact person: first pill (before supporters) jumps to their company /
    // community page, where they edit it and manage members.
    if (isContact && userInfo.company_id) {
      list.push({
        to: `/public-company/${userInfo.company_id}`,
        icon: 'pencil',
        label: userInfo.is_community ? t('editCommunity') : t('editCompany'),
      });
    }

    // The mails we send their people for this event, to read, change and approve. Only when
    // there IS a plan (has_mail_plan from /api/home) and they organise the event.
    if (isContact && userInfo.has_mail_plan && userInfo.event_id) {
      list.push({
        to: `/event-communication/${userInfo.event_id}`,
        icon: 'envelope',
        label: t('mailPlanPill'),
      });
    }

    if (isPlayer) {
      list.push({ to: `/public-team/${userInfo.team_id}`, icon: 'users-alt', label: t('team') });
      // Edit your team straight from Home (name/photo/slogan) — the public
      // team page stays the outward-facing view, no edit affordances there.
      list.push({
        to: '/edit-team',
        icon: 'pencil',
        label: t('editTeam'),
        onClick: () => sessionStorage.setItem('otherTeamId', userInfo.team_id),
      });
    }
    if (isSupporter) {
      list.push({ to: '/find-team', icon: 'search', label: t('findTeam') || t('teams') });
    }
    // A player gets ONE "Supporters" pill, to their own cheers screen — the supporter
    // leaderboard is linked from there. Everyone else has no such screen, so theirs goes
    // straight to the leaderboard.
    if (isCheers && !isPlayer) {
      list.push({ to: '/cheers-leaderboard', icon: 'party-horn', label: t('cheers') });
    }
    if (isCheers && isPlayer) {
      list.push({
        to: '/cheers',
        icon: 'party-horn',
        label: t('cheers'),
        onClick: () => sessionStorage.setItem('otherTeamId', userInfo.team_id),
      });
    }
    if (isContact && isPostEvent) {
      list.push({ to: '/company-readiness', icon: 'list-check', label: t('readiness') });
    }
    if (isPlayer && (upcomingList.length > 0 || isPostEvent)) {
      list.push({ to: '/planning', icon: 'calendar-lines', label: t('planning') });
    }
    if (isPlayer && isPostEvent) {
      list.push({ to: '/scores', icon: 'hundred-points', label: t('scores') });
    }
    if (isPlayer && isPreEvent) {
      list.push({ to: '/bonus-points', icon: 'gift', label: t('bonus') });
    }
    list.push({ to: '/leaderboard', icon: 'ranking-star', label: t('leaderboard') });
    // Its own screen, and only where the event switched it on (Content > Website).
    if (userInfo?.show_who_is_here_tab) {
      list.push({ to: '/who-is-here', icon: 'users', label: t('whoIsHere') });
    }
    if (isCrew) {
      list.push({ to: '/crew-teams', icon: 'employees', label: t('teams') });
    }
    if (isCrew && userInfo.qr_attendance) {
      list.push({ to: '/crew-scan', icon: 'qrcode', label: t('scanCheckIn') });
    }
    if (isCrew) {
      list.push({
        to: crewOpen ? `/crew-details?event=${crewOpen.event_id}` : '/crew-details',
        icon: 'id-badge',
        label: crewOpen ? t('opsMyDetailsNeeded') : t('opsMyDetails'),
      });
    }
    if (hasMissions) {
      list.push({
        to: '/missions',
        icon: 'photo-film-music',
        label: t('missions'),
        onClick: () => sessionStorage.setItem('otherTeamId', userInfo.team_id),
      });
    }
    if (isPlayer) {
      list.push({
        to: '/team-photos',
        icon: 'camera',
        label: t('teamPhotos'),
        onClick: () => sessionStorage.setItem('otherTeamId', userInfo.team_id),
      });
    }
    if (hasAwards) {
      list.push({ to: '/awards', icon: 'medal', label: t('awards') });
    }
    if (hasAlbum) {
      list.push({ to: '/album', icon: 'picture', label: t('photos') });
    }
    if (userInfo?.has_final_instructions) {
      list.push({ to: '/final-instructions', icon: 'document', label: t('finalInstructions') });
    }
    if (userInfo?.venue_map) {
      list.push({ to: '/venue-map', icon: 'map', label: t('venueMap') });
    }
    if (userInfo?.event_id) {
      list.push({ to: `/public-event/${userInfo.event_id}`, icon: 'info', label: t('eventDetails') });
    }
    // The rules are for the people who play; a supporter came to cheer.
    if (!isSupporter) list.push({ to: '/scoring-rules', icon: 'whistle', label: t('rules') });
    list.push({ to: '/faq', icon: 'interrogation', label: t('faq') });
    if (hasSurveys) {
      list.push({ to: '/event-surveys', icon: 'list-check', label: t('surveys') });
    }
    return list;
  }, [
    userInfo,
    isPlayer,
    isCrew,
    isContact,
    isCheers,
    isPreEvent,
    isPostEvent,
    upcomingList.length,
    hasMissions,
    hasAwards,
    hasAlbum,
    hasSurveys,
    crewOpen,
    t,
  ]);

  // Open seats on my team → pushy invite banner right under the action rail:
  // filling the team is the FIRST captain job, before any readiness.
  const [teamSales, setTeamSales] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  useEffect(() => {
    if (!isPlayer || !userInfo?.team_id) { setTeamSales(null); return; }
    apiRequest('GET', '/my-team-sales').then(setTeamSales).catch(() => {});
  }, [isPlayer, userInfo?.team_id]);
  // A fully-bought team invites for FREE (already-paid open seats); a per-seat
  // rate invites via the PAID buy link. Free wins when both apply.
  const inviteFree = !!(teamSales && teamSales.claim_seat_link && (teamSales.open_seats || 0) > 0);
  const inviteLink = inviteFree ? teamSales.claim_seat_link : (teamSales && teamSales.buy_seat_link) || null;
  const inviteOpenSeats = inviteFree
    ? teamSales.open_seats
    : (teamSales && teamSales.buy_seat_link ? Math.max(teamSales.seats_total - teamSales.seats_claimed, 0) : 0);

  // Merge status for my team → adaptive home banner. Same team-scoped fetch as
  // the invite banner above; reuses the exact endpoint PublicTeam's own-team
  // merge indicator already calls, so no backend change is needed.
  const [mergeInfo, setMergeInfo] = useState(null);
  useEffect(() => {
    if (!isPlayer || !userInfo?.team_id) { setMergeInfo(null); return; }
    apiRequest('GET', '/merge-requests').then(setMergeInfo).catch(() => {});
  }, [isPlayer, userInfo?.team_id]);
  const mergeIncoming = mergeInfo?.incoming || [];
  const mergeOpen = !!mergeInfo?.open;
  const mergeIsCaptain = !!mergeInfo?.is_captain;
  // A PLG friends team merges through Meet the Ladies (cards of people, not team names).
  const meetLadies = !!mergeInfo?.meet_the_ladies;
  const mergePath = meetLadies ? '/meet-the-ladies' : '/merge-teams';
  // She bought the SOLO ticket and is still on her own: no friends to invite — her whole Home
  // is about finding her team (card to finish / who wants to team up / who is looking too).
  const solo = mergeInfo?.solo || null;

  // An online payment of hers that failed and can still be paid. Above everything: nothing
  // else on this screen matters while the booking itself is not paid. Silent on failure.
  const [unpaidOrders, setUnpaidOrders] = useState([]);
  useEffect(() => {
    if (!userInfo?.id) { setUnpaidOrders([]); return; }
    apiRequest('GET', '/my-unpaid-orders').then((d) => setUnpaidOrders(d?.orders || [])).catch(() => {});
  }, [userInfo?.id]);

  // Raffle prizes she won and still has to collect. While a draw is under way (raffle_live) Home
  // keeps asking every 20 s, so the "come to the stand now" pop-up reaches a phone that is
  // already open on Home — Tornea has no push. Outside a draw it asks once.
  const [prizesToCollect, setPrizesToCollect] = useState([]);
  const [raffleLive, setRaffleLive] = useState(false);
  useEffect(() => {
    if (!userInfo?.id) { setPrizesToCollect([]); setRaffleLive(false); return undefined; }
    let active = true;
    const load = () => apiRequest('GET', '/my-prizes').then((d) => {
      if (!active) return;
      setPrizesToCollect((d?.prizes || []).filter((p) => p.status === 'drawn'));
      setRaffleLive(!!d?.raffle_live);
    }).catch(() => {});
    load();
    if (!raffleLive) return () => { active = false; };
    const id = setInterval(() => { if (document.visibilityState === 'visible') load(); }, 20000);
    return () => { active = false; clearInterval(id); };
  }, [userInfo?.id, raffleLive]);
  const prizeKey = prizesToCollect.map((p) => p.id).join(',');

  return (
    <Base activeNav="home">
      <ContactPersonChoiceModal show={showCpChoice} onClose={dismissCpChoice} eventId={userInfo?.event_id} />
      <AnnouncementModal refreshKey={prizeKey} />
      <div className="home">
          {/* ============== HERO ============== */}
          {hero.kind === 'loading' ? (
            <div className="home-hero">
              <div className="home-hero__bg" />
              <div className="home-hero__shade" />
              <div className="home-hero__body">
                <div className="home-hero__placeholder" />
                <div className="home-hero__placeholder home-hero__placeholder--sm" />
              </div>
            </div>
          ) : (
            <div className={`home-hero ${hero.contain ? 'home-hero--contain' : ''}`}>
              <div
                className="home-hero__bg"
                style={{ backgroundImage: `url(${hero.image})` }}
              />
              <div className="home-hero__shade" />

              <div className="home-hero__top">
                {hero.kind === 'supporter' ? (
                  <span />
                ) : (() => {
                  const ChipTag = userInfo?.event_id ? Link : 'span';
                  const chipProps = userInfo?.event_id
                    ? { to: `/public-event/${userInfo.event_id}` }
                    : {};
                  return (
                    <ChipTag className="home-hero__chip" {...chipProps}>
                      <img
                        src={userInfo.photo_url || fallback.activity}
                        alt=""
                        className="home-hero__chip-logo"
                      />
                      <div className="home-hero__chip-text">
                        <span className="home-hero__chip-name">{userInfo.name}</span>
                        <span className="home-hero__chip-date">
                          {[formatDate(userInfo.date), userInfo.location_name]
                            .filter(Boolean)
                            .join(' • ')}
                        </span>
                      </div>
                    </ChipTag>
                  );
                })()}

                <Link to="/notifications" className="home-hero__bell" aria-label="Notifications">
                  <Icon name="bell" />
                  <span className="home-hero__bell-dot" />
                </Link>
              </div>

              <Link to={hero.to} className="home-hero__body link">
                <div className="home-hero__kicker">{hero.kicker}</div>
                <Title heading="2" my="0" className="home-hero__title" align="left">
                  {hero.title}
                  {/* The number on the scoresheets — it stays put when the team renames itself. */}
                  {hero.number && <span className="home-hero__number">#{hero.number}</span>}
                </Title>
                {hero.subtitle && (
                  <div className="home-hero__subtitle">{hero.subtitle}</div>
                )}
              </Link>

              <Link to={hero.to} className="home-hero__action" aria-label="Open">
                <Icon name={hero.action} />
              </Link>
            </div>
          )}

          {/* ============== COMPANY / COMMUNITY SWITCHER (contact person) ======
              A person can lead SEVERAL companies/communities but the session holds
              one at a time. When there's more than one, let them pick which one
              they're managing right now (re-scopes the JWT + reloads Home). */}
          {isContact && (userInfo.my_companies?.length > 1) && (
            <div className="home-invite">
              <div className="home-invite__text">
                <b>{t('switchCompanyTitle')}</b>
                <span>{t('switchCompanySub')}</span>
              </div>
              <div className="home-invite__actions">
                <select
                  value={userInfo.company_id || ''}
                  disabled={switching}
                  onChange={(e) => doSwitchContext(
                    userInfo.my_companies.find((c) => c.id === e.target.value))}
                  style={{ padding: '0.5rem 0.75rem', borderRadius: '0.6rem',
                           border: '1px solid rgba(0,0,0,0.15)', maxWidth: '100%',
                           fontSize: '0.9rem', background: '#fff' }}
                >
                  {userInfo.my_companies.map((c) => (
                    <option key={`${c.kind}:${c.id}`} value={c.id}>
                      {c.name}
                      {c.kind === 'bravos'
                        ? ' · Bravos'
                        : (c.is_community ? ` · ${t('communityBadge')}` : '')}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ============== PENDING APPROVALS (contact person) ==============
              People asked to join the company/community and are waiting to be
              accepted. Front and centre, tapping the whole banner jumps to
              Manage members where Accept/Decline live. */}
          {isContact && pendingJoin.length > 0 && (
            <Link to="/manage-employees" className="home-invite home-invite--alert">
              <div className="home-invite__text">
                <b>
                  <span className="home-invite__badge">{pendingJoin.length}</span>
                  {t('pendingJoinTitle')}
                </b>
                <span>{t('pendingJoinSub')}</span>
              </div>
              <div className="home-invite__actions">
                <span className="home-pill">
                  <Icon name="user-check" />
                  <span>{t('reviewRequests')}</span>
                </span>
              </div>
            </Link>
          )}

          {/* ========== GROW YOUR COMMUNITY (community leader) ==========
              FIRST card on a leader's home, and usually the only one she needs: she
              rarely has a team of her own. Three doors, one credential — the booking
              LINK for WhatsApp, an EMAIL invite by name, and the bare CODE for her
              campaigns. Whatever comes in through any of them becomes a team of her
              community, and the women land in her network. Deliberately no "add
              members" / "create extra teams" here: the teams lead their own lives. */}
          {isContact && grow?.code && (
            <div className="home-invite home-grow">
              <div className="home-invite__text">
                <b>{t('growCommunityTitle')}</b>
                <span>{t('growCommunitySub', { community: grow.community?.name || '' })}</span>
              </div>
              <div className="home-invite__actions home-grow__acts">
                <button type="button" className="home-pill home-grow__act"
                        onClick={() => copyGrow('link')}>
                  <Icon name={growCopied === 'link' ? 'check' : 'link'} />
                  <span>{growCopied === 'link' ? t('copied') : t('copyBookingLink')}</span>
                </button>
                <button type="button" className="home-pill home-grow__act"
                        onClick={() => { setInviteOpen((v) => !v); setInviteMsg(null); }}>
                  <Icon name="envelope" />
                  <span>{t('emailAnInvite')}</span>
                </button>
                <button type="button" className="home-pill home-grow__act"
                        onClick={() => { setShowCode((v) => !v); copyGrow('code'); }}>
                  <Icon name={growCopied === 'code' ? 'check' : 'key'} />
                  <span>{growCopied === 'code' ? t('copied') : t('myCommunityCode')}</span>
                </button>
              </div>
              {showCode && (
                <p className="home-grow__code">
                  <code>{grow.code}</code>
                  <span>{t('myCommunityCodeHint')}</span>
                </p>
              )}
              {inviteOpen && (
                <form className="home-grow__invite" onSubmit={submitCommunityInvite}>
                  <input className="auth__input" type="text" required
                         placeholder={t('firstName')} value={inviteForm.first_name}
                         onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })} />
                  <input className="auth__input" type="text"
                         placeholder={t('lastName')} value={inviteForm.last_name}
                         onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })} />
                  <input className="auth__input" type="email" required
                         placeholder={t('email')} value={inviteForm.email}
                         onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} />
                  <button type="submit" className="home-pill" disabled={inviteBusy}>
                    <Icon name="paper-plane" />
                    <span>{inviteBusy ? '…' : t('sendInvite')}</span>
                  </button>
                  {inviteMsg && (
                    <p className={`home-grow__msg${inviteMsg.ok ? '' : ' home-grow__msg--err'}`}>
                      {inviteMsg.text}
                    </p>
                  )}
                </form>
              )}
            </div>
          )}

          {/* ========== MY NETWORK (community leader) ==========
              Who came in through her code — the answer to "is my sharing working?", and the
              reason she does this at all. Shown even when empty: an absent card reads as a
              broken feature, an empty one tells her what to expect. */}
          {isContact && grow?.code && (
            <div className="home-invite">
              <div className="home-invite__text">
                <b>{t('communityMembersTitle')}</b>
                <span>
                  {grow.members?.length
                    ? t('communityMembersSub', { count: grow.members.length })
                    : t('noCommunityMembersYet')}
                </span>
              </div>
              {grow.members?.length > 0 && (
                <div className="home-invite__actions"
                     style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.4rem' }}>
                  {grow.members.map((m) => (
                    <span key={m.id} className="home-pill"
                          style={{ justifyContent: 'space-between', width: '100%' }}>
                      <span><Icon name="user" /> {m.name}</span>
                      <span style={{ opacity: 0.85, fontSize: '0.8rem' }}>
                        {m.since ? formatDate(m.since) : ''}
                      </span>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ========== MY COMMUNITY'S TEAMS (community leader) ==========
              The teams her code brought in. Read-only: each has its own captain and
              runs itself — she is looking at her result, not a roster to manage. Also
              shown empty, for the same reason as the network card above. */}
          {isContact && grow?.code && (
            <div className="home-invite">
              <div className="home-invite__text">
                <b>{t('communityTeamsTitle')}</b>
                <span>
                  {grow.teams?.length
                    ? t('communityTeamsSub', { count: grow.teams.length })
                    : t('noCommunityTeamsYet')}
                </span>
              </div>
              {grow.teams?.length > 0 && (
                <div className="home-invite__actions"
                     style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.4rem' }}>
                  {grow.teams.map((tm) => (
                    <Link key={tm.team_id} to={`/public-team/${tm.team_id}`} className="home-pill"
                          style={{ justifyContent: 'space-between', width: '100%' }}>
                      <span><Icon name="users-alt" /> {tm.name}</span>
                      <span style={{ opacity: 0.85, fontSize: '0.8rem',
                                     display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                        {tm.captain_name || t('noCaptainYet')}
                        <Icon name="users-alt" /> {tm.player_count}
                      </span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============== ADD CAPTAINS (contact person) ==============
              The contact person's first job: a captain on every team. Each
              chip drops straight into that team's players page (otherTeamId),
              where they add a colleague or share the invite link.
              A community leader normally owns NO team, so missing_captain is 0 and
              this never shows for her — it comes back only if she books one herself. */}
          {isContact && captainStatus && captainStatus.missing_captain > 0 && (
            <div className="home-invite">
              <div className="home-invite__text">
                <b>{t('addCaptainsTitle')}</b>
                <span>{t('addCaptainsSub')}</span>
              </div>
              <div className="home-invite__actions">
                {captainStatus.teams.filter((tm) => !tm.has_captain).map((tm) => (
                  <button key={tm.team_id} type="button" className="home-pill"
                          onClick={() => {
                            sessionStorage.setItem('otherTeamId', tm.team_id);
                            navigate('/players');
                          }}>
                    <Icon name="user-crown" />
                    <span>{tm.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* The old "add members / create extra teams" card is GONE (2026-08). A leader
              grows her community by sharing her code, not by building rosters: the teams
              that come in are ordinary teams with their own captains. See the "Grow your
              community" card above. */}

          {/* ========== YOUR TEAMS (contact person) ==========
              A PERSISTENT list of this company/community's teams for the event, so a
              team stays visible after it gets a captain — the add-captains card above
              only surfaces captainLESS ones, so a fully-booked team would otherwise
              vanish from home. Tap a team to manage its players/seats. */}
          {isContact && captainStatus?.teams?.length > 0 && (
            <div className="home-invite">
              <div className="home-invite__text home-invite__text--center">
                <b>{t('yourTeamsTitle')}</b>
                <span>{t('yourTeamsSub')}</span>
              </div>
              <div className="home-invite__actions"
                   style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.4rem' }}>
                {captainStatus.teams.map((tm) => (
                  <button key={tm.team_id} type="button" className="home-pill"
                          style={{ justifyContent: 'space-between', width: '100%' }}
                          onClick={() => {
                            sessionStorage.setItem('otherTeamId', tm.team_id);
                            navigate('/players');
                          }}>
                    <span><Icon name="users-alt" /> {tm.name}</span>
                    <span style={{ opacity: 0.85, fontSize: '0.8rem',
                                   display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      {tm.captain_name || t('noCaptainYet')}
                      <Icon name="users-alt" /> {tm.player_count}
                      {tm.open_seats > 0 ? ` (+${tm.open_seats})` : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ========== YOUR TEAMS (a buyer of several) ==========
              Checkout claims the buyer's own seat on the FIRST team of the basket
              and stops, so a "2 × team of 4" order leaves a second team with no
              captain, no players and nobody who can see it. One row per team the
              buyer's order paid for; tapping one opens its Players screen, where the
              invite link lives. Below two teams there is nothing to say — that team
              is the one they are already on. */}
          {isPlayer && orderTeams.length > 1 && (
            <div className="home-invite">
              <div className="home-invite__text home-invite__text--center">
                <b>{t('yourTeamsTitle')}</b>
                <span>{t('yourTeamsSub')}</span>
              </div>
              <div className="home-invite__actions"
                   style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.4rem' }}>
                {orderTeams.map((tm) => (
                  <button key={tm.team_id} type="button" className="home-pill"
                          style={{ justifyContent: 'space-between', width: '100%' }}
                          onClick={() => {
                            sessionStorage.setItem('otherTeamId', tm.team_id);
                            navigate('/players');
                          }}>
                    <span><Icon name="users-alt" /> {tm.name}</span>
                    <span style={{ opacity: 0.85, fontSize: '0.8rem',
                                   display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                      {tm.captain_name || t('noCaptainYet')}
                      <Icon name="users-alt" /> {tm.player_count}
                      {tm.open_seats > 0 ? ` (+${tm.open_seats})` : ''}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ========== YOUR TICKETS (contact person) ==========
              What the company bought, and how much of it still has nobody in it.
              "Claimed of total" is the headline because an unclaimed seat is a
              colleague who has not been added yet — the number that needs acting on.
              Hidden entirely for a company that holds no tickets. */}
          {isContact && ticketSummary?.seats_total > 0 && (
            <div className="home-invite">
              <div className="home-invite__text">
                <b>{t('yourTickets')}</b>
                <span>
                  {t('seatsClaimedOfTotal', {
                    claimed: (ticketSummary.seats_total || 0) - (ticketSummary.seats_open || 0),
                    total: ticketSummary.seats_total || 0,
                  })}
                  {ticketSummary.seats_open > 0
                    ? ` · ${t('openSeatsCount', { n: ticketSummary.seats_open })}`
                    : ''}
                </span>
              </div>
              <div className="home-invite__actions">
                <button type="button" className="home-pill" onClick={() => navigate('/shop/tickets')}>
                  <Icon name="ticket" />
                  <span>{t('seeAll')}</span>
                </button>
                <button type="button" className="home-pill" onClick={() => navigate('/shop')}>
                  <Icon name="shopping-cart" />
                  <span>{t('shop')}</span>
                </button>
              </div>
            </div>
          )}

          {/* ============== INVITE — open seats on my team ==============
              THE first thing a fresh captain sees, above everything else.
              Only for ticket-based team orders: the banner needs a
              buy_seat_link, which only exists when the team's seats came
              from an order whose rate opted into per-seat selling — HT/FAB
              teams have no ticket rows, so nothing ever shows there. */}
          {/* ============== NOT PAID YET — a failed online payment ==============
              /api/pay/<order> mints a fresh Stripe session (or shows the receipt when it was
              paid in the meantime), so this is a plain link, not a call. */}
          {unpaidOrders.map((o) => (
            <a key={o.order_id} href={`/api/pay/${o.order_id}`} className="home-merge home-merge--active">
              <div className="home-merge__icon"><Icon name="credit-card" /></div>
              <div className="home-merge__body">
                <p className="home-merge__title">{t('unpaidHomeTitle')}</p>
                <p className="home-merge__sub">{t('unpaidHomeSub', { event: o.event_name, amount: o.amount })}</p>
              </div>
              <span className="home-merge__chevron"><Icon name="angle-right" /></span>
            </a>
          ))}

          {/* ============== RAFFLE — a prize to collect, right now ============== */}
          {prizesToCollect.map((p) => (
            <Link key={p.id} to="/my-prizes" className="home-merge home-merge--active">
              <div className="home-merge__icon"><Icon name="gift" /></div>
              <div className="home-merge__body">
                <p className="home-merge__title">{t('prizeHomeTitle', { prize: p.prize_name })}</p>
                <p className="home-merge__sub">{t('prizeHomeSub')}</p>
              </div>
              <span className="home-merge__chevron"><Icon name="angle-right" /></span>
            </Link>
          ))}

          {/* ============== MEET THE LADIES — a solo lady, still on her own ==============
              Takes the invite card's place: she came alone precisely because she has
              nobody to invite. The loudest thing wins: an answer she owes, then her
              unfinished card, then who is looking too. */}
          {solo && (
            <Link to="/meet-the-ladies"
                  className={`home-merge ${solo.incoming?.length ? 'home-merge--active' : 'home-merge--soft'}`}>
              <div className="home-merge__icon"><Icon name="heart" /></div>
              <div className="home-merge__body">
                {solo.incoming?.length > 0 ? (
                  <>
                    <p className="home-merge__title">
                      {t('soloHomeIncomingTitle', { name: solo.incoming[0] })}
                      {solo.incoming.length > 1 && <span className="home-merge__count">{solo.incoming.length}</span>}
                    </p>
                    <p className="home-merge__sub">{t('soloHomeIncomingSub')}</p>
                  </>
                ) : solo.missing?.length > 0 ? (
                  <>
                    <p className="home-merge__title">{t('soloHomeCardTitle')}</p>
                    <p className="home-merge__sub">{t('soloHomeCardSub')}</p>
                  </>
                ) : (
                  <>
                    <p className="home-merge__title">{t('meetTheLadies')}</p>
                    <p className="home-merge__sub">
                      {solo.ladies > 0
                        ? t('soloHomeLookingSub', { count: solo.ladies })
                        : t('soloHomeFirstSub')}
                    </p>
                    {solo.faces?.length > 0 && (
                      <div className="d-flex mt-1">
                        {solo.faces.map((src, i) => (
                          <img key={i} src={src} alt=""
                               style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover',
                                        border: '2px solid #fff', marginLeft: i ? -8 : 0 }} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              <span className="home-merge__chevron"><Icon name="angle-right" /></span>
            </Link>
          )}

          {!solo && inviteOpenSeats > 0 && inviteLink && (
            <div className="home-invite">
              <div className="home-invite__text home-invite__text--center">
                <b>{inviteFree ? t('inviteFreeTitle') : t('inviteFriendsTitle')}</b>
                <span>
                  {t('seatsTaken', { taken: teamSales.seats_claimed, total: teamSales.seats_total })}
                  {' · '}{inviteFree ? t('inviteFreeExplainer') : t('inviteCopyExplainer')}
                </span>
              </div>
              <div className="home-invite__actions">
                <input className="home-invite__input" readOnly value={inviteLink}
                       onFocus={(e) => e.target.select()} />
                <button type="button" className="home-pill" onClick={() => {
                  // Copy a warm message + link so it's ready to send, not a bare URL.
                  navigator.clipboard.writeText(`${t('teamSeatShareText')}\n${inviteLink}`);
                  setInviteCopied(true);
                  setTimeout(() => setInviteCopied(false), 2500);
                }}>
                  <Icon name="copy" />
                  <span>{inviteCopied ? t('copied', { defaultValue: 'Copied!' }) : t('copyLink')}</span>
                </button>
              </div>
              {/* Install the branded app right where the onboarding happens.
                  Own full-width row UNDER the copy line — in the actions row
                  it fell off the edge on mobile. */}
              <InstallApp className="home-pill home-invite__install" />
            </div>
          )}

          {/* ============== MERGE STATUS — open for merge / request received ==============
              Adaptive: an incoming request wins as a loud, tappable banner; else a
              subtle "open for merge" note. Everyone on the team sees a received
              request (transparency); only the captain can decide, on /merge-teams. */}
          {solo ? null : mergeIncoming.length > 0 ? (
            <div className="home-merge home-merge--active"
                 role="button" tabIndex={0}
                 onClick={() => navigate(mergePath)}
                 onKeyDown={(e) => { if (e.key === 'Enter') navigate(mergePath); }}>
              <div className="home-merge__icon"><Icon name="users" /></div>
              <div className="home-merge__body">
                <p className="home-merge__title">
                  {t('mergeIncomingTitle')}
                  <span className="home-merge__count">{mergeIncoming.length}</span>
                </p>
                <p className="home-merge__sub">
                  {t('mergeIncomingFrom', { team: mergeIncoming[0].name })}
                  {mergeIncoming.length > 1 && ` ${t('mergeMoreCount', { count: mergeIncoming.length - 1 })}`}
                  {' · '}
                  {mergeIsCaptain ? t('mergeIncomingDecide') : t('mergeIncomingWaitCaptain')}
                </p>
              </div>
              <span className="home-merge__chevron"><Icon name="angle-right" /></span>
            </div>
          ) : mergeOpen ? (
            <Link to={mergePath} className="home-merge home-merge--soft">
              <div className="home-merge__icon"><Icon name="users" /></div>
              <div className="home-merge__body">
                <p className="home-merge__title">{meetLadies ? t('meetTheLadies') : t('mergeOpenTitle')}</p>
                <p className="home-merge__sub">{meetLadies ? t('meetOpenSub') : t('mergeOpenSub')}</p>
              </div>
              <span className="home-merge__chevron"><Icon name="angle-right" /></span>
            </Link>
          ) : (meetLadies && mergeInfo?.has_room) ? (
            <Link to="/meet-the-ladies" className="home-merge home-merge--soft">
              <div className="home-merge__icon"><Icon name="heart" /></div>
              <div className="home-merge__body">
                <p className="home-merge__title">{t('meetTheLadies')}</p>
                <p className="home-merge__sub">{t('meetHomeSub')}</p>
              </div>
              <span className="home-merge__chevron"><Icon name="angle-right" /></span>
            </Link>
          ) : null}

          {/* ============== COUNTDOWN BANNER ============== */}
          {isPreEvent && (() => {
            // Clicking the countdown opens the event page (same target as the
            // hero name chip). Fall back to a plain div if we have no event id.
            const CountdownTag = userInfo?.event_id ? Link : 'div';
            const countdownProps = userInfo?.event_id
              ? { to: `/public-event/${userInfo.event_id}` }
              : {};
            return (
              <CountdownTag className="home-countdown" {...countdownProps}>
                <div className="home-countdown__inner">
                  <div className="home-countdown__label">{t('upcoming') || 'Starts in'}</div>
                  <Countdown countdownDate={userInfo.date} />
                </div>
              </CountdownTag>
            );
          })()}

          {/* ============== TEAM RECAP (PLG) ============== */}
          {/* Before the event it is the teaser that gets the profile filled in: the team sees its
              own recap with every empty spot marked. After the event it is the recap itself, with
              the PDF next to it: the watch link covers the tile (stretched), the download sits above. */}
          {isPlayer && userInfo?.team_id && userInfo?.business_model === 'PLG' && (
            <div className="home-recap">
              <span className={`home-recap__photo ${userInfo.team_photo_url ? '' : 'is-empty'}`}>
                {userInfo.team_photo_url
                  ? <img src={userInfo.team_photo_url} alt="" />
                  : <Icon name="camera" />}
              </span>
              <span className="home-recap__body">
                <span className="home-recap__title">
                  {isPreEvent ? t('recapHomeTitle') : t('recapWatch')}
                </span>
                {isPreEvent && <span className="home-recap__sub">{t('recapHomeSub')}</span>}
                <span className="home-recap__actions">
                  <Link to={`/public-team/${userInfo.team_id}/recap`} className="home-recap__cta home-recap__watch">
                    <Icon name="play" pack="sr" /> {isPreEvent ? t('recapHomeCta') : t('recapWatch')}
                  </Link>
                  {!isPreEvent && (
                    <>
                      <a className="home-recap__cta home-recap__download"
                         href={`/api/team-recap/${userInfo.team_id}/slides.pdf?lang=${encodeURIComponent((i18n.language || '').slice(0, 2))}`}>
                        <Icon name="file-pdf" /> {t('recapDownloadPdf')}
                      </a>
                      <RecapImagesButton teamId={userInfo.team_id} className="home-recap__cta home-recap__download" />
                    </>
                  )}
                </span>
              </span>
            </div>
          )}

          {/* ============== WHATSAPP GROUP ============== */}
          {userInfo?.whatsapp_url && (
            <a
              className="home-whatsapp"
              href={userInfo.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="whatsapp" pack="brands" />
              <span>{t('joinWhatsapp')}</span>
            </a>
          )}
          {/* The brand's WhatsApp community — every player and contact, not one team. Not for a
              supporter: WhatsApp is for the people who play (owner, 2026-10-26). */}
          {userInfo?.community_whatsapp_url && !isSupporter && (
            <a
              className="home-whatsapp"
              href={userInfo.community_whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Icon name="whatsapp" pack="brands" />
              <span>{t('joinCommunityWhatsapp')}</span>
            </a>
          )}

          {/* ============== BREAKOUT ============== */}
          {/* "You're in group 3", right where the check-in QR is — the two belong to the same
              five minutes. Null whenever the host has not split the room, so nothing draws.
              `userInfo?.` because userInfo is null on the first render (see the networking note
              below: this file has no ErrorBoundary above it). */}
          <MyBreakout breakout={userInfo?.breakout} />

          {/* ============== QR CHECK-IN ============== */}
          <CheckInCard active={(isPlayer || isSupporter) && !!userInfo.qr_attendance} />

          {/* ============== THE OTHER EVENT (same day, same venue) ============== */}
          {(userInfo?.sibling_events || []).map((other) => (
            <button
              key={other.id}
              type="button"
              className="home-rules-cta home-sibling-cta"
              onClick={() => doSwitchSibling(other.id)}
              disabled={siblingSwitching}
            >
              <div className="home-rules-cta__icon"
                   style={other.primary_color ? { background: other.primary_color } : undefined}>
                {other.logo
                  ? <img src={other.logo} alt="" className="home-sibling-cta__logo" />
                  : <Icon name="shuffle" />}
              </div>
              <div className="home-rules-cta__body">
                <p className="home-rules-cta__title">{t('siblingEventTitle')}</p>
                <p className="home-rules-cta__sub">{t('siblingEventSub', { name: other.name })}</p>
              </div>
              <span className="home-rules-cta__chevron">
                {siblingSwitching ? <span className="auth__spinner" /> : <Icon name="angle-right" />}
              </span>
            </button>
          ))}

          {/* ============== NETWORKING QR ============== */}
          {/* userInfo is null on the FIRST render — the fetch has not answered yet — so every
              read here has to be optional. The neighbours survive by accident: their `isPlayer`
              / `isContact` guards are themselves `userInfo?.role`, so they short-circuit before
              touching userInfo. This line had nothing in front of it, threw on the very first
              paint, and with no ErrorBoundary in the app that unmounts the whole tree — a blank
              white screen on /home for every role and every business unit. */}
          {!!userInfo?.networking_enabled && !!userInfo?.event_id && (
            <button
              type="button"
              className="home-meet"
              onClick={() => setShowMeetQr(true)}
            >
              <Icon name="user-add" />
              <span>{t('meetShowQr')}</span>
            </button>
          )}
          {showMeetQr && (
            <MeetQrSheet eventId={userInfo.event_id} onClose={() => setShowMeetQr(false)} />
          )}

          {/* ============== PRE-EVENT SURVEY ============== */}
          {/* The questions the host is still waiting on, in the last 24 hours before the door.
              Beside the networking QR because both belong to that same day, and above the quiz
              surveys below because those are a team's points while this is a question somebody
              asked this person. No date test here: the server sends the list on the last day and
              an empty one every other (routes/event.py `_pending_pre_surveys`), and it only ever
              holds what this reader has NOT answered yet — the same call the day-before mail
              names them with. The path already carries `?event=`, which is what files the answer
              under this event; `/f/<slug>` is the survey's own published page, in-app, exactly
              as the member home's polls open. `userInfo?.` for the reason spelled out on the QR
              above: userInfo is null on the first paint and this file has no ErrorBoundary. */}
          {(userInfo?.pre_surveys || []).map((s) => (
            <Link key={s.id} className="home-next" to={s.path}>
              <div
                className="home-next__media"
                style={{
                  backgroundImage: `url(${require('./../../assets/images/fallback/survey.png')})`,
                }}
              />
              <div className="home-next__body">
                <div className="home-next__kicker">{t('preSurveyKicker')}</div>
                <div className="home-next__title">{s.name}</div>
                <div className="home-next__time">
                  <Icon name="clock" pack="sr" />
                  {t('preSurveyNote')}
                </div>
              </div>
              <span className="home-next__arrow">
                <Icon name="angle-right" />
              </span>
            </Link>
          ))}

          {/* ===== OPEN SURVEYS (player-only reminder) =====
              In-window surveys the player's team hasn't completed yet.
              Stacked above the "Up next" featured card using the same
              home-next hero styling. Tapping a card mirrors the
              EventSurveys page: stash the survey id in sessionStorage,
              then navigate to /survey. */}
          {incompleteSurveys.map((s) => {
            const endDate = s.end_date ? new Date(s.end_date) : null;
            return (
              <Link
                key={s.id}
                className="home-next"
                to="/survey"
                onClick={() => sessionStorage.setItem('selectedSurveyId', s.id)}
              >
                <div
                  className="home-next__media"
                  style={{
                    backgroundImage: `url(${s.picture_url || require('./../../assets/images/fallback/survey.png')})`,
                  }}
                />
                <div className="home-next__body">
                  <div className="home-next__kicker">{t('surveys')}</div>
                  <div className="home-next__title">{s.name}</div>
                  {endDate && (
                    <div className="home-next__time">
                      <Icon name="clock" pack="sr" />
                      {endDate.toLocaleDateString()}
                    </div>
                  )}
                </div>
                <span className="home-next__arrow">
                  <Icon name="angle-right" />
                </span>
              </Link>
            );
          })}

          {/* ============== UP NEXT (featured) ============== */}
          {featuredNext && (
            <Link
              className="home-next"
              to="/score"
              onClick={() => sessionStorage.setItem('selectedScoreId', featuredNext.id)}
            >
              <div
                className="home-next__media"
                style={{
                  backgroundImage: `url(${featuredNext.photo_url || fallback.activity})`,
                }}
              />
              <div className="home-next__body">
                <div className="home-next__kicker">{t('upcomingChallenges')}</div>
                <div className="home-next__title">{featuredNext.name}</div>
                <div className="home-next__time">
                  <Icon name="clock" pack="sr" />
                  {featuredNext.start_time?.match(/\d{2}:\d{2}/)?.[0] || ''}
                </div>
              </div>
              <span className="home-next__arrow">
                <Icon name="angle-right" />
              </span>
            </Link>
          )}

          {/* ============== LIVE RANK CARD ============== */}
          {isPlayer && teamRank && (
            <Link to="/leaderboard" className="home-rank">
              <div className="home-rank__pos">
                <span className="home-rank__pos-num">
                  <span className="home-rank__hash">#</span>{teamRank.pos}
                </span>
                <span className="home-rank__pos-of">
                  {t('of', 'of')} {teamRank.total}
                </span>
              </div>
              <div className="home-rank__body">
                <div className="home-rank__kicker">{t('leaderboard')}</div>
                <p className="home-rank__title">{teamRank.name}</p>
                <div className="home-rank__points">
                  <strong>{teamRank.points}</strong> {t('points')}
                </div>
              </div>
              <span className="home-rank__icon">
                <Icon name="ranking-star" />
              </span>
            </Link>
          )}

          {/* ============== SUPPORTERS CARD ==============
              The rank card's twin in a colour that is in NO brand — PLG's primary is pink, so a
              pink card vanished under a rank card that already glows pink. How many supporters
              the team has and its place on the supporter board (where the card leads); the strip
              underneath goes to the share screen, which is how the number grows. */}
          {isPlayer && isCheers && userInfo.team_id && (
            <div className="home-supporters">
              <Link to="/cheers-leaderboard" className="home-rank home-rank--supporters">
                <div className="home-rank__pos">
                  <span className="home-rank__pos-num">{ownCheerCount}</span>
                  <span className="home-rank__pos-of">{t('homeSupporters.count')}</span>
                </div>
                <div className="home-rank__body">
                  <div className="home-rank__kicker">{t('cheersLeaderboard')}</div>
                  <p className="home-rank__title">
                    {ownCheerCount > 0 && ownCheerRank
                      ? t('homeSupporters.rank', { rank: ownCheerRank.pos, total: ownCheerRank.total })
                      : t('homeSupporters.none')}
                  </p>
                </div>
                <span className="home-rank__icon">
                  <Icon name="party-horn" />
                </span>
              </Link>
              <Link
                to="/cheers"
                onClick={() => sessionStorage.setItem('otherTeamId', userInfo.team_id)}
                className="home-supporters__share"
              >
                <Icon name="share" />
                <span>{t('homeSupporters.share')}</span>
                <Icon name="angle-right" />
              </Link>
            </div>
          )}

          {/* ============== STAT TILES ============== */}
          {(() => {
            const tiles = [];
            if (isPlayer && isPreEvent) {
              tiles.push(
                <StatTile
                  key="bonus"
                  tone="orange"
                  icon="gift"
                  to="/bonus-points"
                  num={userInfo.bonus_total || 0}
                  label={t('extraPointsEarned')}
                />
              );
            }
            if (showReadiness && readiness) {
              tiles.push(
                <StatTile
                  key="readiness"
                  tone="teal"
                  to="/team-readiness"
                  ring={readiness?.readiness}
                  num={`${readiness?.readiness}%`}
                  label={t('teamReadiness')}
                />
              );
            }
            if (isContact && isPreEvent) {
              tiles.push(
                <StatTile
                  key="avg"
                  tone="teal"
                  icon="list-check"
                  to="/company-readiness"
                  num={`${userInfo.avg_readiness ?? 0}%`}
                  label={t('avgTeamReadiness')}
                />
              );
            }
            if (isCheers && isPlayer) {
              tiles.push(
                <StatTile
                  key="cheers"
                  tone="pink"
                  icon="party-horn"
                  to="/cheers"
                  onClick={() => sessionStorage.setItem('otherTeamId', userInfo.team_id)}
                  num={ownCheerCount}
                  label={t('cheers')}
                />
              );
            }
            // Personal shareable badge — sits next to the votes/cheers badge.
            if (isPlayer && userInfo.team_id) {
              tiles.push(
                <StatTile
                  key="badge"
                  tone="purple"
                  className="stat-tile--action"
                  icon="id-badge"
                  to="/badge"
                  label={t('shareBadge')}
                />
              );
            }
            return tiles.length > 0 ? <div className="stat-tiles">{tiles}</div> : null;
          })()}

          {/* ============== QUICK ACTION RAIL ============== */}
          {pills.length > 0 && (
            <div className="home-rail">
              {pills.map((p, i) => (
                <Link
                  key={`${p.to}-${i}`}
                  to={p.to}
                  onClick={p.onClick}
                  className="home-pill"
                >
                  <Icon name={p.icon} />
                  <span>{p.label}</span>
                </Link>
              ))}
              {/* Add to the phone's home screen — lives in the invite banner
                  when that shows; here as fallback so it always has a spot. */}
              {inviteOpenSeats === 0 && <InstallApp />}
            </div>
          )}

          {/* ============== MORE UPCOMING ============== */}
          {restUpcoming.length > 0 && (
            <Section icon="clock" title={t('upcomingChallenges')} to="/planning">
              {restUpcoming.map((ch) => (
                <Tile
                  key={ch.id}
                  to="/score"
                  onClick={() => sessionStorage.setItem('selectedScoreId', ch.id)}
                  image={ch.photo_url || fallback.activity}
                  title={ch.name}
                  badge={{
                    icon: 'clock',
                    pack: 'sr',
                    text: ch.start_time?.match(/\d{2}:\d{2}/)?.[0] || '',
                  }}
                />
              ))}
            </Section>
          )}

          {/* ============== MISSIONS (photo + video together) ============== */}
          {hasMissions && (
            <Section icon="photo-film-music" title={t('missions')} to="/missions">
              {activeMissions.map((s) => (
                <Tile
                  key={`m-${s.id}`}
                  to="/mission"
                  onClick={(e) => {
                    e.stopPropagation();
                    sessionStorage.setItem('missionId', s.id);
                  }}
                  image={s.submission?.image_url || s.photo_url || fallback.mission}
                  title={s.name}
                />
              ))}
              {activeVideoMissions.map((s) => (
                <Tile
                  key={`sm-${s.id}`}
                  to="/social-mission"
                  onClick={(e) => {
                    e.stopPropagation();
                    sessionStorage.setItem('missionId', s.id);
                  }}
                  image={s.photo_url || fallback.mission}
                  title={s.name}
                />
              ))}
            </Section>
          )}

          {/* ============== TEAMS ============== */}
          {userInfo?.show_teams_tab && homeTeams?.length > 0 && (
            <Section
              icon="employees"
              title={t(isContact ? 'yourTeams' : 'teams')}
              to={isContact ? '/company-readiness' : '/teams'}
              seeAllKey={isContact ? 'viewReadiness' : 'seeAll'}
            >
              {homeTeams.map((team) => {
                const img = teamPhoto(team.team_picture, team.company_picture);
                const contain = !team.team_picture && !!team.company_picture;
                return (
                  <Tile
                    key={team.team_id}
                    to={`/public-team/${team.team_id}`}
                    image={img}
                    contain={contain}
                    title={team.team_name}
                    // A community-booked team has no company, so the sub-line carries
                    // "via <community>" instead — its own name stays the headline.
                    sub={team.company_name
                      || (team.community_name
                        ? t('viaCommunity', { community: team.community_name })
                        : '')}
                  />
                );
              })}
            </Section>
          )}

          {/* ============== PARTICIPANTS ============== */}
          {homeParticipants?.length > 0 && (
            <Section icon="users" title={t('participants')} to="/participants">
              {homeParticipants.map((person) => (
                <Tile
                  key={person.id}
                  to={`/u/${person.id}`}
                  image={person.photo_url || localStorage.getItem('defaultPlayerPhoto') || fallback.user}
                  title={person.full_name}
                  sub={person.team_name}
                />
              ))}
            </Section>
          )}

          {/* ============== COMPANIES ============== */}
          {userInfo?.show_companies_tab && !userInfo?.organizer_id && homeCompanies?.length > 0 && (
            <Section icon="building" title={t('companies')} to="/companies">
              {homeCompanies.map((company) => (
                <Tile
                  key={company.id}
                  to={`/public-company/${company.id}`}
                  image={company.photo_url || fallback.company}
                  contain
                  title={company.name}
                />
              ))}
            </Section>
          )}

          {/* ============== COMMUNITIES ============== */}
          {userInfo?.show_communities_tab && !userInfo?.organizer_id && homeCommunities?.length > 0 && (
            <Section icon="heart" title={t('communities')} to="/communities">
              {homeCommunities.map((community) => (
                <Tile
                  key={community.id}
                  to={`/public-company/${community.id}`}
                  image={community.photo_url || fallback.company}
                  contain
                  title={community.name}
                />
              ))}
            </Section>
          )}

          {/* ============== BATTLES ============== */}
          {homeBattles?.length > 0 && (
            <Section icon="running" title={t('challenges')} to="/battles">
              {homeBattles.map((battle) => (
                <Tile
                  key={battle.id}
                  to="/battle"
                  onClick={() => sessionStorage.setItem('selectedBattleId', battle.id)}
                  image={battle.photo_url || fallback.activity}
                  title={battle.name}
                />
              ))}
            </Section>
          )}

          {/* ============== CONTACT PERSON NOTICE ============== */}
          <ContactPersonNotice userInfo={userInfo} messageKey="contactPersonHomeNoticeTitle" />

          {/* ============== "HOW DOES SCORING WORK?" CTA ============== */}
          {/* Not for a supporter, like the Rules pill: they came to cheer. */}
          {!isSupporter && (
            <Link to="/scoring-rules" className="home-rules-cta">
              <div className="home-rules-cta__icon">
                <Icon name="whistle" />
              </div>
              <div className="home-rules-cta__body">
                <p className="home-rules-cta__title">{t('homeRulesPromptTitle')}</p>
                <p className="home-rules-cta__sub">{t('homeRulesPromptSub')}</p>
              </div>
              <span className="home-rules-cta__chevron">
                <Icon name="angle-right" />
              </span>
            </Link>
          )}

          {/* The player's own invite code (PLG / HT): friends claim a spot on the early list. */}
          {userInfo?.referral_code && (
            <MyReferralCode code={userInfo.referral_code} brand={userInfo.name} compact />
          )}

          {/* ============== REFER A FRIEND CTA (bottom, all roles) ============== */}
          <Link to="/refer" className="home-rules-cta">
            <div className="home-rules-cta__icon">
              <Icon name="user-add" />
            </div>
            <div className="home-rules-cta__body">
              <p className="home-rules-cta__title">{t('referFriend')}</p>
              <p className="home-rules-cta__sub">{t('referFriendCtaSub')}</p>
            </div>
            <span className="home-rules-cta__chevron">
              <Icon name="angle-right" />
            </span>
          </Link>

          {/* ============== BLOGS (latest few + "Show more") ============== */}
          {blogs.length > 0 && (
            <div className="home-blogs">
              <div className="home-section__head">
                <h3 className="home-section__title">
                  <Icon name="newspaper" theme="primary" /> {t('latestContent')}
                </h3>
              </div>
              {blogs.slice(0, HOME_BLOG_COUNT).map((it, i) => (
                <a
                  key={i}
                  className="home-blog"
                  href={it.url || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {it.image && (
                    <span
                      className="home-blog__thumb"
                      style={{ backgroundImage: `url("${it.image}")` }}
                    />
                  )}
                  <span className="home-blog__body">
                    <span className="home-blog__title">{it.title}</span>
                    {it.summary && <span className="home-blog__summary">{it.summary}</span>}
                  </span>
                  <Icon name="angle-right" className="home-blog__chev" />
                </a>
              ))}
              {blogs.length > HOME_BLOG_COUNT && (
                <button
                  type="button"
                  className="home-blogs__more"
                  onClick={() => navigate('/blogs')}
                >
                  {t('showMore')} <Icon name="angle-right" />
                </button>
              )}
            </div>
          )}

          {/* ============== SPONSORS ============== */}
          {sponsors?.length > 0 && (
            <div className="home-sponsors">
              <div className="home-sponsors__label">{t('sponsoredBy')}</div>
              {/* One column per sponsor type, most important first (the API
                  orders them), each cut off from the next by a rule; untyped
                  sponsors trail as a last, unlabelled column. The wall clips
                  the row's overhanging left edge so the rule vanishes on the
                  first column of every wrapped line -- see the stylesheet. */}
              <div className="home-sponsors__wall">
                <div className="home-sponsors__row">
                  {groupSponsorsByType(sponsors).map((group) => (
                    <div className="home-sponsors__group" key={group.type || '_untyped'}>
                      {group.type && (
                        <div className="home-sponsors__type">{group.type}</div>
                      )}
                      <div className="home-sponsors__grid">
                        {group.items.map((s) => (
                          // A sponsor with its own URL links out; the rest keep the
                          // internal public-company page. The key carries the type too:
                          // a sponsor tagged with several types appears in each group.
                          s.url ? (
                            <a
                              key={`${group.type || ''}-${s.id}`}
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="home-sponsors__item"
                              style={{
                                backgroundImage: `url(${s.photo_url || fallback.company})`,
                              }}
                              aria-label={s.name || ''}
                            />
                          ) : (
                            <Link
                              key={`${group.type || ''}-${s.id}`}
                              to={`/public-company/${s.id}`}
                              className="home-sponsors__item"
                              style={{
                                backgroundImage: `url(${s.photo_url || fallback.company})`,
                              }}
                              aria-label={s.name || ''}
                            />
                          )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
    </Base>
  );
};

export default Home;
